import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { FormCheckboxList } from "components/form-fields";
import { ShowHide } from "components/layout";
import { Modal, ModalSize, NotificationToaster } from "components/notifications";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { GreyFleetService } from 'services';

const badRequestCode = 400;

const defaultInvalidateReasonList = [
    { id: "nameMismatch", name: "Incorrect named driver", checked: false },
    { id: "vehicleMismatch", name: "Incorrect vehicle registration", checked: false },
    { id: "startDateMismatch", name: "Policy start date", checked: false },
    { id: "endDateMismatch", name: "Policy end date", checked: false },
    { id: "businessUse", name: "The required type(s) not covered", checked: false }
];

export function GreyFleetInsuranceInvalidationModal({
    isOpen,
    onClose,
    onSuccess,
    onNext,
    insuranceDetails,
    actionInProgress,
    setActionInProgress,
    nextInsuranceId = null
}) {
    const [invalidateReason, setInvalidateReason] = useState(defaultInvalidateReasonList);
    const reasonSelected = useMemo(() => invalidateReason.some(item => item.checked), [invalidateReason]);

    useEffect(() => {
        if (isOpen) {
            const updatedList = invalidateReason.map(item => {
                item.checked = false;
                return item;
            });
            setInvalidateReason(updatedList);
        }
    }, [isOpen]);

    function onInvalidateReasonChange(event, id) {
        const updatedList = invalidateReason.map(item => {
            if (item.id === id) {
                item.checked = event.target.checked;
            }
            return item;
        });
        setInvalidateReason(updatedList);
    }

    function invalidateInsurance(withNext = false) {
        if (actionInProgress || !insuranceDetails) {
            return;
        }
        
        setActionInProgress(true);

        const reason = invalidateReason.reduce((acc, item) => {
            acc[item.id] = !!item.checked;
            return acc;
        }, {});

        GreyFleetService.invalidateInsurance(insuranceDetails.insuranceDocumentId, reason)
            .then(
                () => {
                    NotificationToaster.show(Intent.SUCCESS, "Insurance invalidated successfully.");
                    if (withNext) {
                        if (onNext) {
                            onNext();
                        } else {
                            onSuccess();
                        }
                    } else {
                        onSuccess();
                    }
                },
                (error) => {
                    if (error?.response?.status === badRequestCode) {
                        NotificationToaster.show(Intent.WARNING, error.response.data);
                    } else {
                        NotificationToaster.show(Intent.DANGER, "Insurance cannot be invalidated. Please try again.");
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed invalidating insurance.");
            })
            .finally(() => {
                setActionInProgress(false);
            });
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Invalidate Insurance"}
            size={ModalSize.SMALL}
            clickOutsideClose={!actionInProgress}
            showCloseButton={false}
            footer={
                <div className="inline-items push-right">
                    <Button text="Cancel" intent={Intent.NONE} onClick={onClose} disabled={actionInProgress}></Button>
                    <Button text="Invalidate" intent={Intent.PRIMARY} onClick={() => invalidateInsurance(false)} disabled={actionInProgress || !reasonSelected}></Button>
                    <ShowHide
                        evaluator={!!nextInsuranceId}
                        show={<Button text="Invalidate And Next" intent={Intent.PRIMARY} onClick={() => invalidateInsurance(true)} disabled={actionInProgress || !reasonSelected}></Button>}
                    />
                </div>
            }
        >
            <span className="text-intent">Select one or more of the following criteria to invalidate this insurance:</span>
            <div className="spacer-top">
                <FormCheckboxList
                    items={invalidateReason}
                    onChange={onInvalidateReasonChange}
                />
            </div>
            <ShowHide
                evaluator={!!nextInsuranceId}
                show={
                    <Fragment>
                        <h3>Next Insurance Check ID</h3>
                        <span className="text-intent-info">{nextInsuranceId ?? 0}</span>
                    </Fragment>
                }
            />
        </Modal>
    );
}

GreyFleetInsuranceInvalidationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onNext: PropTypes.func,
    insuranceDetails: PropTypes.object,
    actionInProgress: PropTypes.bool.isRequired,
    setActionInProgress: PropTypes.func.isRequired,
    nextInsuranceId: PropTypes.number
};
